import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Calendar, ArrowRight, Tag } from 'lucide-react';
import { blogPosts as mockBlogPosts } from '../mockData';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Blog = () => {
  const [blogPosts, setBlogPosts] = useState(mockBlogPosts);

  useEffect(() => {
    // Try to fetch from database, fall back to mock data
    const fetchBlogs = async () => {
      try {
        const response = await fetch(`${API_URL}/api/blogs`);
        if (response.ok) {
          const data = await response.json();
          // If database has posts, use them; otherwise keep mock data
          if (data.length > 0) {
            setBlogPosts(data);
          }
        }
      } catch (error) {
        console.log('Using mock blog data');
      }
    };
    fetchBlogs();
  }, []);

  // Blog listing schema
  const blogSchema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Hair Extensions Training Academy Blog",
    "description": "Expert advice, industry insights, and career guidance for hair professionals",
    "url": "https://hairextensionstraining.academy/blog",
    "publisher": {
      "@type": "Organization",
      "name": "Hair Extensions Training Academy",
      "logo": {
        "@type": "ImageObject",
        "url": "https://customer-assets.emergentagent.com/job_a235b419-86b2-4667-9fd0-c39959dca4de/artifacts/he7jel3z_hair%20extensions%20training%20academy%20logo.png"
      }
    },
    "blogPost": blogPosts.slice(0, 10).map(post => ({
      "@type": "BlogPosting",
      "headline": post.title,
      "description": post.excerpt,
      "image": post.image,
      "datePublished": post.date,
      "author": {
        "@type": "Organization",
        "name": "Hair Extensions Training Academy"
      }
    }))
  };

  return (
    <div className="page-container" data-testid="blog-page">
      <Helmet>
        <title>Blog & Insights | Hair Extensions Training Academy</title>
        <meta name="description" content="Expert advice, industry insights, and career guidance for hair professionals. Learn about hair extensions, hair loss solutions, and building your beauty career." />
        <link rel="canonical" href="https://hairextensionstraining.academy/blog" />
        <script type="application/ld+json">{JSON.stringify(blogSchema)}</script>
      </Helmet>

      {/* Hero Section */}
      <section className="blog-hero">
        <div className="content-wrapper text-center-content">
          <h1 className="page-title">Blog & Insights</h1>
          <p className="page-subtitle">
            Expert advice, industry insights, and career guidance for hair professionals
          </p>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="section-container">
        <div className="content-wrapper">
          <div className="blog-grid">
            {blogPosts.map((post) => (
              <article key={post.id} className="blog-card" data-testid={`blog-card-${post.id}`}>
                <Link to={`/blog/${post.slug}`} className="blog-card-link">
                  <div className="blog-card-image">
                    <img src={post.image} alt={post.title} loading="lazy" />
                  </div>
                  <div className="blog-card-content">
                    <div className="blog-card-meta">
                      <span className="blog-card-date">
                        <Calendar size={14} />
                        {new Date(post.date).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </span>
                    </div>
                    <h2 className="blog-card-title">{post.title}</h2>
                    <p className="blog-card-excerpt">{post.excerpt}</p>
                    <div className="blog-card-tags">
                      {post.tags.slice(0, 2).map((tag, index) => (
                        <span key={index} className="blog-tag">
                          <Tag size={12} />
                          {tag}
                        </span>
                      ))}
                    </div>
                    <span className="blog-card-read-more">
                      Read Article <ArrowRight size={16} />
                    </span>
                  </div>
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-container cta-section">
        <div className="content-wrapper">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Start Your Training?</h2>
            <p className="cta-description">
              Take the first step towards a rewarding career in hair extensions and hair loss solutions
            </p>
            <Link to="/courses" className="btn-primary-large">
              View Our Courses <ArrowRight size={24} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog;
